import {
  Button,
  CheckboxCard,
  CheckboxWrapper,
  Divider,
  Icon,
} from '@design-system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { normaliseVariantName } from '@utils/frames';
import classNames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  FF_FRAMING_CAROUSEL,
  FF_FRAMING_PRESELECTION,
} from '../../../constants/featureFlags';
import { useDialogContext } from '../../../hooks/useDialogContext';
import { CurrencyCode } from '../../../types/Locations';
import { Frame, FrameOptionConsolidated } from '../../../types/Products';
import { Image } from '../../Image';
import { ProductPrice } from '../../ProductPrice/ProductPrice';
import { GlazingSvg } from './GlazingSvg';

interface FramePickerProps {
  currencyCode?: CurrencyCode;
  description: string;
  frameByDefault?: boolean;
  frameOptions: Array<FrameOptionConsolidated>;
  frameSelected: Frame | null;
  hideSubmitButton: boolean;
  hideTitle: boolean;
  includeUnframed: boolean;
  onFrameSelected?: (variant: Frame | null) => void;
  onGetInTouchClick: () => void;
  onSubmit?: (variant: Frame | null) => void;
  onVariantSelected?: (variant: Frame | null) => void;
  productSubtotal: number;
  submitButtonLabel: string;
}

const FramePicker = ({
  currencyCode,
  description,
  frameByDefault,
  frameOptions,
  frameSelected,
  hideSubmitButton,
  hideTitle,
  includeUnframed,
  onFrameSelected,
  onGetInTouchClick,
  onSubmit,
  onVariantSelected,
  productSubtotal,
  submitButtonLabel,
}: FramePickerProps) => {
  const { t } = useTranslation(['product', 'countries']);
  const { hideDialog, showDialog } = useDialogContext();
  const checkboxGroupNameFrame = useMemo(() => crypto.randomUUID(), []);
  const checkboxGroupNameGlazing = useMemo(() => crypto.randomUUID(), []);

  const experimentalFramingCarousel = useFeatureIsOn(FF_FRAMING_CAROUSEL);
  const experimentalFramingPreselection = useFeatureIsOn(
    FF_FRAMING_PRESELECTION,
  );

  const [selectedFrame, setSelectedFrame] =
    useState<FrameOptionConsolidated | null>(
      frameOptions.find(({ title }) => title === frameSelected?.title) || null,
    );
  const [selectedVariant, setSelectedVariant] = useState<string | null>(
    frameSelected?.variant.title ?? null,
  );
  const [frame, setFrame] = useState<Frame | null>(frameSelected);

  const framePrice = useMemo(() => {
    if (!frame) return 0;

    return frame.variant.consolidatedVariants.reduce(
      (acc, { price, quantity }) => acc + price * quantity,
      0,
    );
  }, [frame]);

  useEffect(() => {
    if (selectedFrame === null) {
      setFrame(null);
      return;
    }
    const { variants: submitVariants, ...selectedFramesRest } = selectedFrame;
    const submitVariant = submitVariants.find(
      ({ title }) => title === selectedVariant,
    );

    if (!submitVariant) {
      setFrame(null);
      return;
    }

    const newFrame = {
      ...selectedFramesRest,
      variant: submitVariant,
    };

    setFrame(newFrame);

    if (onFrameSelected) {
      onFrameSelected(newFrame);
    }

    if (onVariantSelected) {
      onVariantSelected(newFrame);
    }
  }, [selectedFrame, selectedVariant, onFrameSelected, onVariantSelected]);

  const subtotal = selectedVariant
    ? productSubtotal +
      (frame?.variant.consolidatedVariants.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0,
      ) ?? 0)
    : productSubtotal;

  useEffect(() => {
    if (onFrameSelected) {
      onFrameSelected(frame);
    }
  }, [frame, onFrameSelected]);

  useEffect(() => {
    if (frameSelected === null) {
      setSelectedFrame(
        experimentalFramingPreselection && frameOptions.length > 0
          ? frameOptions[0]
          : null,
      );
      setSelectedVariant(
        experimentalFramingPreselection && frameOptions.length > 0
          ? frameOptions[0].variants[0].title
          : null,
      );
      return;
    }

    const newSelectedFrame =
      frameOptions.find(({ title }) => title === frameSelected.title) || null;
    setSelectedFrame(newSelectedFrame);
    setSelectedVariant(frameSelected.variant.title);
  }, [frameOptions, frameSelected, experimentalFramingPreselection]);

  useEffect(() => {
    if (!frameByDefault) return;

    if (selectedFrame === null && frameOptions.length > 0) {
      const defaultFrame = frameOptions[0];
      setSelectedFrame(defaultFrame);
      setSelectedVariant(defaultFrame.variants[0].title);
    }
  }, [frameByDefault, frameOptions, selectedFrame]);

  const handleShowGlazingGuide = () =>
    showDialog(
      <div className="relative flex w-full flex-col overflow-y-scroll rounded-t-lg bg-white p-6 md:max-w-md md:overflow-y-auto md:rounded-lg">
        <div className="text-center text-md font-bold">
          {t('drawers.review.framing.upgrade.guide.title')}
        </div>
        <div className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center">
          <Button
            hideLabel
            icon="ic_close"
            label="Close"
            onClick={hideDialog}
            size="sm"
            tabIndex={0}
            type="button"
            variant="transparent"
          />
        </div>
        <div className="pt-2 md:pt-5">
          <GlazingSvg />
          {selectedFrame?.variants.map((v, index) => {
            const normalisedVariantName = normaliseVariantName(v.title);
            const frameDescription = t(
              `drawers.review.framing.upgrade.${normalisedVariantName}.description`,
            );
            const frameVariantFeatures = t(
              `drawers.review.framing.upgrade.${normalisedVariantName}.details`,
              {
                returnObjects: true,
              },
            );

            return (
              <div
                key={v.title}
                className={classNames('flex w-full flex-col', {
                  'mt-8': index > 0,
                  'mt-5': index === 0,
                })}
              >
                <div className="font-bold">{v.title}</div>
                <div>{frameDescription}</div>
                {Array.isArray(frameVariantFeatures) &&
                  frameVariantFeatures.length > 0 && (
                    <div className="mt-3">
                      {frameVariantFeatures.map((feature, key) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={`features-${key}`}
                          className="flex w-full flex-row"
                        >
                          <Icon name="ic_check" />
                          <span className="overflow-hidden text-ellipsis">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>,
      { overlayOtherDialogs: true },
    );

  const showDescriptions = description.length > 0;

  const carouselRef = useRef<Splide>(null);
  const [carouselLength, setCarouselLength] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);

  return (
    <>
      {!hideTitle ? (
        <div className="mb-6 hidden w-full flex-1 md:flex">
          <p className="text-md font-bold md:text-lg">
            {t('drawers.review.framing.title')}
          </p>
        </div>
      ) : null}
      <div className="flex flex-col gap-9">
        {experimentalFramingCarousel ? (
          <div className="flex flex-col gap-3">
            <p className="font-bold">
              {t('drawers.review.framing.carouselTitle')}
            </p>
            <Splide
              ref={carouselRef}
              hasTrack={false}
              onMove={(_, index) => setCarouselIndex(index)}
              onReady={(s) => {
                setCarouselLength(s.length);
              }}
              options={{
                rewind: true,
                arrows: false,
                pagination: false,
              }}
            >
              <SplideTrack>
                <SplideSlide>
                  <div className="flex aspect-[348/192] flex-col justify-end rounded-sm bg-[url('https://images.avantarte.org/media/31pQqP1ICxp7IniRsIdUZ1KgT4v')] bg-cover bg-center bg-no-repeat p-4 text-white">
                    <p className="font-bold">
                      {t('drawers.review.framing.carouselSlideOneTitle')}
                    </p>
                    <p>{t('drawers.review.framing.carouselSlideOneBody')}</p>
                  </div>
                </SplideSlide>
                <SplideSlide>
                  <div className="flex aspect-[348/192] flex-col justify-end rounded-sm bg-[url('https://images.avantarte.org/media/31pQqN3gCRb2weCIujH4qqLbMqG')] bg-cover bg-center bg-no-repeat p-4 text-white">
                    <p className="font-bold">
                      {t('drawers.review.framing.carouselSlideTwoTitle')}
                    </p>
                    <p>{t('drawers.review.framing.carouselSlideTwoBody')}</p>
                  </div>
                </SplideSlide>
                <SplideSlide>
                  <div className="flex aspect-[348/192] flex-col justify-end rounded-sm bg-[url('https://images.avantarte.org/media/31pQqKL8wy0wB70UCng1FhRmXHX')] bg-cover bg-center bg-no-repeat p-4 text-white">
                    <p className="font-bold">
                      {t('drawers.review.framing.carouselSlideThreeTitle')}
                    </p>
                    <p>{t('drawers.review.framing.carouselSlideThreeBody')}</p>
                  </div>
                </SplideSlide>
                <SplideSlide>
                  <div className="flex aspect-[348/192] flex-col justify-end rounded-sm bg-[url('https://images.avantarte.org/media/31pQqJyd5AwopYbLmxiFdTQNAFR')] bg-cover bg-center bg-no-repeat p-4 text-white">
                    <p className="font-bold">
                      {t('drawers.review.framing.carouselSlideFourTitle')}
                    </p>
                    <p>{t('drawers.review.framing.carouselSlideFourBody')}</p>
                  </div>
                </SplideSlide>
              </SplideTrack>
            </Splide>

            <nav className="flex justify-center gap-3">
              <button
                className="flex size-9 items-center justify-center rounded-full border"
                onClick={() => carouselRef.current?.splide?.go('<')}
                type="button"
              >
                <span className="sr-only">Previous slide</span>
                <Icon name="ic_arrow_left" size="sm" />
              </button>
              <ul className="flex items-center gap-2">
                {Array.from({ length: carouselLength }, (_, i) => (
                  <li key={`pagination-${i}`} className="flex items-center">
                    <button
                      className={classNames(
                        'size-2 rounded-full transition duration-300 ease-out',
                        i === carouselIndex
                          ? 'bg-neutral-6'
                          : 'bg-neutral-6/10 hover:bg-neutral-6/40',
                      )}
                      onClick={() => carouselRef.current?.splide?.go(i)}
                      type="button"
                    >
                      <span className="sr-only">{i + 1}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                className="flex size-9 items-center justify-center rounded-full border"
                onClick={() => carouselRef.current?.splide?.go('>')}
                type="button"
              >
                <span className="sr-only">Next slide</span>
                <Icon name="ic_arrow_right" size="sm" />
              </button>
            </nav>
          </div>
        ) : null}
        <div className="flex flex-col gap-4">
          <div>
            <p className="font-bold">{t('drawers.review.framing.subtitle')}</p>
            {showDescriptions ? <p>{description}</p> : null}
          </div>

          <CheckboxWrapper>
            {includeUnframed && !experimentalFramingPreselection ? (
              <CheckboxCard
                key={`frameOptions-${checkboxGroupNameFrame}-unframed`}
                checked={selectedFrame == null}
                className={`${frameByDefault ? 'order-last' : ''}`}
                id={`frameOptions-${checkboxGroupNameFrame}-EMPTY`}
                label={t('drawers.review.framing.unframedTitle')}
                name={`frameOptions-${checkboxGroupNameFrame}`}
                onChange={() => {
                  setSelectedFrame(null);
                  setSelectedVariant(null);
                }}
                slotVisibleOnChecked
                type="radio"
                value="EMPTY"
              />
            ) : null}
            {frameOptions.map((frameOption) => {
              const selectedFrameOption = frameOptions.find(
                (fo) => fo.title === frame?.title,
              );
              const checked = frameOption.title === selectedFrameOption?.title;

              const cheapestUpgradeOption = Math.min(
                ...frameOption.variants.map((variant) =>
                  variant.consolidatedVariants.reduce(
                    (acc, { price, quantity }) => acc + price * quantity,
                    0,
                  ),
                ),
              );

              const cheapestUpgradeSelected = selectedFrameOption
                ? Math.min(
                    ...selectedFrameOption.variants.map((v) =>
                      v.consolidatedVariants.reduce(
                        (acc, { price, quantity }) => acc + price * quantity,
                        0,
                      ),
                    ),
                  )
                : 0;
              const price = cheapestUpgradeOption - cheapestUpgradeSelected;

              return (
                <CheckboxCard
                  key={`frameOptions-${checkboxGroupNameFrame}-${frameOption.title}`}
                  checked={checked}
                  description={frameOption.description}
                  id={`frameOptions-${checkboxGroupNameFrame}-${frameOption.title}`}
                  label={frameOption.title}
                  name={`frameOptions-${checkboxGroupNameFrame}`}
                  onChange={() => {
                    setSelectedFrame(frameOption);
                    setSelectedVariant(frameOption.variants[0].title);
                  }}
                  slot={
                    <div className="grid w-full grid-cols-2 gap-3">
                      {frameOption.images.slice(0, 2).map((image) => (
                        <Image
                          key={image.src}
                          alt={image.alt}
                          src={image.src}
                          width={384}
                        />
                      ))}
                    </div>
                  }
                  slotVisibleOnChecked
                  suffix={
                    checked ? undefined : (
                      <ProductPrice
                        currencyCode={currencyCode}
                        prefix={price >= 0 ? '+' : '-'}
                        price={Math.abs(price)}
                        skipConvertCurrency={!!currencyCode}
                      />
                    )
                  }
                  type="radio"
                  value={frameOption.title}
                />
              );
            })}
          </CheckboxWrapper>
        </div>
        {selectedFrame && selectedFrame.variants.length > 1 ? (
          <div>
            <p className="font-bold">
              {t('drawers.review.framing.upgrade.title')}
            </p>

            <p className="col-span-12 pb-3 md:pb-4">
              {t('drawers.review.framing.upgrade.subTitle')}{' '}
              <button
                className="hyperlink"
                onClick={handleShowGlazingGuide}
                type="button"
              >
                {t('drawers.review.framing.upgrade.guideLink')}
              </button>
            </p>

            <CheckboxWrapper>
              {selectedFrame?.variants.map((variant) => {
                const extraPrice = variant.consolidatedVariants.reduce(
                  (acc, { price, quantity }) => acc + price * quantity,
                  0,
                );
                const price = extraPrice - framePrice;
                const normalisedVariantName = normaliseVariantName(
                  variant.title,
                );
                const frameVariantFeatures = t(
                  `drawers.review.framing.upgrade.${normalisedVariantName}.detailsCheckbox`,
                  {
                    returnObjects: true,
                  },
                );
                const frameFeatures = Array.isArray(frameVariantFeatures)
                  ? frameVariantFeatures.filter(
                      (feature): feature is string =>
                        typeof feature === 'string',
                    )
                  : undefined;

                const checked = selectedVariant === variant.title;

                return (
                  <CheckboxCard
                    key={`glazingOptions-${checkboxGroupNameGlazing}-${variant.title}`}
                    checked={checked}
                    features={frameFeatures}
                    id={`glazingOptions-${checkboxGroupNameGlazing}-${variant.title}`}
                    label={variant.title}
                    name={`glazingOptions-${checkboxGroupNameGlazing}`}
                    onChange={() => setSelectedVariant(variant.title)}
                    suffix={
                      checked ? undefined : (
                        <ProductPrice
                          currencyCode={currencyCode}
                          prefix={price >= 0 ? '+' : '-'}
                          price={Math.abs(price)}
                          skipConvertCurrency={!!currencyCode}
                        />
                      )
                    }
                    type="radio"
                    value={variant.title}
                  />
                );
              })}
            </CheckboxWrapper>
          </div>
        ) : null}
        <dl className="flex justify-between">
          <dt>{t('drawers.review.framing.upgrade.subtotal')}</dt>
          <dd>
            <ProductPrice
              currencyCode={currencyCode}
              price={subtotal}
              skipConvertCurrency={!!currencyCode}
            />
          </dd>
        </dl>
        {hideSubmitButton || !onSubmit ? null : (
          <div className="flex flex-col gap-3">
            <Button
              fullWidth
              label={submitButtonLabel}
              onClick={() => onSubmit(frame)}
              variant="primary"
            />
            {experimentalFramingPreselection ? (
              <Button
                fullWidth
                label={t('drawers.review.framing.continueUnframed')}
                onClick={() => onSubmit(null)}
                variant="tertiary"
              />
            ) : null}
          </div>
        )}
        <Divider />
        <div className="w-full text-neutral-4">
          <Trans i18nKey="product.drawers.review.framing.note" t={t}>
            Do you need help choosing a frame for your piece?{' '}
            <button
              className="hyperlink"
              onClick={onGetInTouchClick}
              type="button"
            >
              Get in touch
            </button>
          </Trans>
        </div>
      </div>
    </>
  );
};

export { FramePicker };
